import {
  Grid,
  Typography,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  Box,
  FormControlLabel,
  Checkbox,
  Chip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonIcon from "@mui/icons-material/Person";
import { getStatusColorAndVariant } from "../../../Utils/utils";

const OrderDetailsForm = ({
  formDetails,
  handleFormChange,
  notifyPicker,
  setNotifyPicker,
  customMessage,
  setCustomMessage,
}) => {
  const FormField = ({ label, value, children }) => (
    <>
      <Grid item xs={4}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "Lexend",
            fontSize: "14px",
            fontWeight: 500,
            color: "#667085",
          }}
        >
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        {children || (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              fontFamily: "Lexend",
              fontSize: "14px",
              fontWeight: 500,
              color: "#252525",
            }}
          >
            {value || ""}
          </Typography>
        )}
      </Grid>
    </>
  );

  const selectedPicker = formDetails?.pickers?.find(
    (picker) => picker.picker_id === formDetails?.picker,
  );

  return (
    <Grid container rowSpacing={2} columnSpacing={12} sx={{ mb: 2 }}>
      <Grid item xs={12} md={6}>
        <Grid container spacing={2}>
          <FormField label="SSS Job Number" value={formDetails?.job_id} />
          <FormField label="Cust Order" value={formDetails?.order_no} />
          <FormField label="Customer" value={formDetails?.customer} />
          <FormField label="Priority">
            <Select
              value={formDetails?.priority || ""}
              onChange={handleFormChange}
              displayEmpty
              size="small"
              name="priority"
              fullWidth
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(0, 0, 0, 0.12)",
                },
              }}
              disabled={!formDetails?.is_update_order}
            >
              <MenuItem value="">Select Priority</MenuItem>
              {formDetails?.orderPriorities?.map((priority) => (
                <MenuItem key={priority.code} value={priority.code}>
                  {priority.name}
                </MenuItem>
              ))}
            </Select>
          </FormField>
<FormField label="SSS ERP Status" value={formDetails?.erp_order_status} />

          {formDetails?.invoice_number && (
            <FormField
              label="Invoice Number"
              value={formDetails?.invoice_number}
            />
          )}
        </Grid>
      </Grid>

      <Grid item xs={12} md={6}>
        <Grid container spacing={2}>
          <FormField label="Date" value={formDetails?.order_date} />
          {formDetails?.order_released_date && (
            <FormField
              label="Released Date"
              value={formDetails?.order_released_date}
            />
          )}
          <FormField label="Cutoff" value={formDetails?.cutoff_date_time} />
          <FormField label="Delivery Date" value={formDetails?.delivery_date} />
          <FormField
            label="Picker Instruction"
            value={formDetails?.delivery_details || "-"}
          />
          <FormField label="Bay" value={formDetails?.bay} />
          <Grid item xs={4}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Lexend",
                fontSize: "14px",
                fontWeight: 500,
                color: "#667085",
              }}
            >
              Picker
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <Autocomplete
              value={selectedPicker || null}
              onChange={(event, newValue) => {
                handleFormChange({
                  target: {
                    name: "picker",
                    value: newValue ? newValue.picker_id : "",
                  },
                });
              }}
              options={formDetails?.pickers || []}
              getOptionLabel={(option) => option.picker_name || ""}
              renderOption={(props, option) => {
                const { color, chipColor } = getStatusColorAndVariant(
                  option.app_status_color,
                );
                return (
                  <Box
                    component="li"
                    {...props}
                    aria-label={`${option.picker_name}, status: ${option.app_status}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PersonIcon
                        sx={{ color: chipColor, fontSize: 20, flexShrink: 0 }}
                        aria-hidden="true"
                      />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.picker_name}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: "#667085" }}>
                      -
                    </Typography>
                    <Chip
                      label={option.app_status}
                      size="small"
                      color={color}
                      sx={{
                        flexShrink: 0,
                        fontWeight: 500,
                        fontSize: "0.75rem",
                      }}
                    />
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Select Picker"
                  inputProps={{
                    ...params.inputProps,
                    "aria-label": "Select a picker",
                  }}
                />
              )}
              popupIcon={<KeyboardArrowDownIcon />}
              disabled={!formDetails?.is_update_order}
              fullWidth
            />

            {formDetails?.status === "In Queue" &&
              selectedPicker &&
              selectedPicker.app_status !== "Online" && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor:
                      selectedPicker.app_status === "Offline"
                        ? "#fdf8f1ff"
                        : "#fcf4f4ff",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    position: "relative",
                  }}
                >
                  {/* Triangular tip/arrow */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: -8,
                      left: 16,
                      width: 0,
                      height: 0,
                      borderLeft: "8px solid transparent",
                      borderRight: "8px solid transparent",
                      borderBottom: (theme) =>
                        `8px solid ${theme.palette.divider}`,
                    }}
                  />
                  {/* Another layer for the inner triangle to match background color */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: -7,
                      left: 16,
                      width: 0,
                      height: 0,
                      borderLeft: "7px solid transparent",
                      borderRight: "7px solid transparent",
                      borderBottom: (theme) =>
                        `7px solid ${theme.palette.grey[100]}`,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color:
                        selectedPicker.app_status === "Offline"
                          ? "#e65100"
                          : "#c62828",
                      flex: 1,
                    }}
                  >
                    Please note that {selectedPicker.picker_name} is{" "}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        display: "inline",
                      }}
                    >
                      {selectedPicker.app_status}
                    </Typography>
                    .
                  </Typography>
                </Box>
              )}

            {formDetails?.picker &&
              formDetails?.notify_order_button_enabled === 1 && (
                <>
                  <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={notifyPicker}
                          onChange={(e) => setNotifyPicker(e.target.checked)}
                          size="small"
                          sx={{ py: 0.5 }}
                          inputProps={{
                            "aria-label": "Notify selected picker",
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 400, color: "#667085" }}
                        >
                          Notify selected picker
                        </Typography>
                      }
                      sx={{
                        ml: -1,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                  </Box>
                  {notifyPicker && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Custom Message"
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        inputProps={{
                          maxLength: 200,
                          "aria-label": "Custom message for picker",
                        }}
                        placeholder="Enter a custom message for the picker (max 200 characters)"
                      />
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 1 }}
                        aria-live="polite"
                      >
                        {customMessage?.length || 0}/200 characters
                      </Typography>
                    </Box>
                  )}
                </>
              )}
          </Grid>
          {formDetails?.order_hold_reason && (
            <FormField label="Reason" value={formDetails?.order_hold_reason} />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default OrderDetailsForm;
